import React, { useState, useMemo } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Plus, Edit, Trash2, Linkedin, MessageCircle, Users, Heart, Send, Upload, Download, RefreshCw, Clock, X } from 'lucide-react'
import { useLinkedInProfiles, useCreateLinkedInProfile, useUpdateLinkedInProfile, useDeleteLinkedInProfile, LinkedInProfile } from '@/hooks/useLinkedInProfiles'
import { useClients } from '@/hooks/useClients'
import { useDashboard } from '@/contexts/DashboardContext'
import { LinkedInMetrics } from './LinkedInMetrics'
import { LinkedInAnalyticsSection } from './LinkedInAnalyticsSection'
import { LinkedInTable } from './LinkedInTable'
import { AdvancedFilter, FilterRule } from '@/components/ui/advanced-filter'
import { useToast } from '@/hooks/use-toast'

export function LinkedInManagement() {
  const { data: profiles = [], isLoading, refetch } = useLinkedInProfiles()
  const { data: clients = [] } = useClients()
  const { state } = useDashboard()
  const selectedClientId = state.selectedClient
  const createProfile = useCreateLinkedInProfile()
  const updateProfile = useUpdateLinkedInProfile()
  const deleteProfile = useDeleteLinkedInProfile()
  const { toast } = useToast()
  
  const [editingProfile, setEditingProfile] = useState<LinkedInProfile | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<FilterRule[]>([])
  const [lastGlobalSync, setLastGlobalSync] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  
  const [formData, setFormData] = useState<LinkedInFormData>({
    sender_name: '',
    linkedin_url: '',
    daily_connections: 40,
    daily_inmails: 40,
    daily_likes: 100,
    daily_direct_messages: 40,
    client_id: '',
    client_name: ''
  })

  // Get last global sync time - update whenever profiles change or when manually refreshed
  const updateLastSync = React.useCallback(() => {
    if (profiles.length > 0) {
      const lastSyncTimes = profiles
        .filter(p => p.last_synced_at)
        .map(p => new Date(p.last_synced_at!).getTime())
      
      if (lastSyncTimes.length > 0) {
        const mostRecent = Math.max(...lastSyncTimes)
        setLastGlobalSync(new Date(mostRecent).toISOString())
      } else {
        // If no last_synced_at, use current time as fallback
        setLastGlobalSync(new Date().toISOString())
      }
    }
  }, [profiles])

  React.useEffect(() => {
    updateLastSync()
  }, [updateLastSync])

  // Update last sync when refetch completes
  const handleRefetch = React.useCallback(async () => {
    await refetch()
    // Small delay to ensure profiles are updated
    setTimeout(() => {
      updateLastSync()
    }, 500)
  }, [refetch, updateLastSync])

  const formatLastSync = (syncTime: string) => {
    if (!syncTime) return 'Never'
    
    const now = new Date()
    const sync = new Date(syncTime)
    const diffInMinutes = Math.floor((now.getTime() - sync.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
    return `${Math.floor(diffInMinutes / 1440)} days ago`
  }

  // Apply filters and search
  const filteredProfiles = useMemo(() => {
    // Resolve numeric client_id from selected client UUID
    let targetClientId: string | null = null
    if (selectedClientId && selectedClientId !== 'all') {
      // Find the client by UUID to get its numeric client_id
      const selectedClient = clients.find(c => c.id === selectedClientId)
      if (selectedClient) {
        // linkedin_profiles.client_id stores numeric client_id as text
        // Handle both cases: client_id as number or as string
        const numericId = selectedClient.client_id
        targetClientId = numericId ? numericId.toString() : null
      }
    }

    return profiles.filter(profile => {
      // Apply client filter from header (dashboard context)
      if (targetClientId) {
        // Match numeric client_id (stored as text in linkedin_profiles)
        // Also handle case where selectedClientId might be the numeric ID directly (for Yotpo where id="5559")
        if (profile.client_id !== targetClientId && profile.client_id !== selectedClientId) {
          return false
        }
      }

      // Apply search query
      if (searchQuery) {
        const search = searchQuery.toLowerCase()
        const matchesSearch = 
          profile.sender_name.toLowerCase().includes(search) ||
          profile.linkedin_url.toLowerCase().includes(search) ||
          (profile.client_name && profile.client_name.toLowerCase().includes(search))
        
        if (!matchesSearch) return false
      }

      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        if (statusFilter === 'ACTIVE' && profile.platform_status !== 'active') return false
        if (statusFilter === 'INACTIVE' && profile.platform_status === 'active') return false
        if (statusFilter === 'SYNC_PENDING' && profile.sync_status !== 'pending') return false
        if (statusFilter === 'SYNC_FAILED' && profile.sync_status !== 'failed') return false
      }

      return true
    })
  }, [profiles, selectedClientId, clients, searchQuery, statusFilter, filters])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.sender_name.trim() || !formData.linkedin_url.trim()) {
      toast({
        title: "Validation Error",
        description: 'Sender name and LinkedIn URL are required',
        variant: "destructive"
      })
      return
    }

    try {
      if (editingProfile) {
        await updateProfile.mutateAsync({
          id: editingProfile.id,
          ...formData
        })
      } else {
        await createProfile.mutateAsync({
          ...formData,
          connections_accepted: 0,
          direct_messages_sent: 0,
          inmails_sent: 0,
          sync_status: 'pending',
          needs_sync: true,
          platform_status: 'active'
        })
      }

      setShowDialog(false)
      resetForm()
    } catch (error) {
      console.error('Error saving LinkedIn profile:', error)
    }
  }

  const handleDelete = async (profileId: string, senderName: string) => {
    if (!confirm(`Are you sure you want to delete LinkedIn profile "${senderName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await deleteProfile.mutateAsync(profileId)
    } catch (error) {
      console.error('Error deleting LinkedIn profile:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      sender_name: '',
      linkedin_url: '',
      daily_connections: 40,
      daily_inmails: 40,
      daily_likes: 100,
      daily_direct_messages: 40,
      client_id: '',
      client_name: ''
    })
    setEditingProfile(null)
  }

  const handleInputChange = (field: keyof LinkedInFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleClientChange = (clientId: string) => {
    const client = clients.find(c => c.id === clientId)
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      client_name: client?.name || ''
    }))
  }

  const handleEdit = (profile: LinkedInProfile) => {
    setEditingProfile(profile)
    setFormData({
      sender_name: profile.sender_name,
      linkedin_url: profile.linkedin_url,
      daily_connections: profile.daily_connections,
      daily_inmails: profile.daily_inmails,
      daily_likes: profile.daily_likes,
      daily_direct_messages: profile.daily_direct_messages,
      client_id: profile.client_id || '',
      client_name: profile.client_name || ''
    })
    setShowDialog(true)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status === statusFilter ? null : status)
  }

  const handleChartFilter = (filterType: string, filterValue: string) => {
    if (filterType === 'client_name') {
      setSearchQuery(filterValue)
    } else if (filterType === 'platform_status') {
      handleStatusFilter(filterValue.toUpperCase())
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LinkedIn Management</h1>
          <p className="text-gray-600 text-sm">
            Manage LinkedIn profiles and outreach capacity ({profiles.length} profiles)
          </p>
          {lastGlobalSync && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              Last sync: {formatLastSync(lastGlobalSync)}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} className="shadow-sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open)
            if (!open) resetForm()
          }}>
            <DialogTrigger asChild>
              <Button className="shadow-sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Profile
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingProfile ? 'Edit LinkedIn Profile' : 'Add LinkedIn Profile'}
                </DialogTitle>
                <DialogDescription>
                  {editingProfile ? 'Update LinkedIn profile information' : 'Add a new LinkedIn profile for outreach'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sender_name">Sender Name *</Label>
                    <Input
                      id="sender_name"
                      value={formData.sender_name}
                      onChange={(e) => handleInputChange('sender_name', e.target.value)}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="linkedin_url">LinkedIn URL *</Label>
                    <Input
                      id="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/johnsmith"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client</Label>
                    <Select value={formData.client_id} onValueChange={handleClientChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="daily_connections">Daily Connections</Label>
                    <Input
                      id="daily_connections"
                      type="number"
                      value={formData.daily_connections}
                      onChange={(e) => handleInputChange('daily_connections', parseInt(e.target.value))}
                      min="1"
                      max="100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="daily_inmails">Daily InMails</Label>
                    <Input
                      id="daily_inmails"
                      type="number"
                      value={formData.daily_inmails}
                      onChange={(e) => handleInputChange('daily_inmails', parseInt(e.target.value))}
                      min="1"
                      max="50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="daily_direct_messages">Daily Direct Messages</Label>
                    <Input
                      id="daily_direct_messages"
                      type="number"
                      value={formData.daily_direct_messages}
                      onChange={(e) => handleInputChange('daily_direct_messages', parseInt(e.target.value))}
                      min="1"
                      max="50"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingProfile ? 'Update Profile' : 'Add Profile'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Status Filter */}
      {statusFilter && (
        <div className="mb-4">
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700 font-medium">
              Active Filter: {statusFilter.replace('_', ' ')}
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setStatusFilter(null)}
              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-8">
          <LinkedInMetrics profiles={profiles} />
        </div>
      </div>

      {/* Analytics Section */}
      <LinkedInAnalyticsSection 
        profiles={profiles} 
        onFilterChange={handleChartFilter}
      />

      {/* LinkedIn Profiles Table */}
      <LinkedInTable
        profiles={filteredProfiles}
        isLoading={isLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filters}
        onFiltersChange={setFilters}
        selectedProfiles={selectedProfiles}
        onSelectionChange={setSelectedProfiles}
        onProfileUpdate={() => {
          refetch()
          updateLastSync()
        }}
        onEditProfile={handleEdit}
        onSyncComplete={() => updateLastSync(true)}
      />
    </div>
  )
}

interface LinkedInFormData {
  sender_name: string
  linkedin_url: string
  daily_connections: number
  daily_inmails: number
  daily_likes: number
  daily_direct_messages: number
  client_id: string
  client_name: string
}
